/* 畫面元件。設計沿用原型（app/src/styles/app.css），程式以 React 重寫，
   所有文字都經過 i18n —— 沒有任何一條文案寫在元件裡。 */
import { useEffect, useState } from "react";
import type { ReactNode } from "react";
import type { Category, Concept, KnowledgeLink, Settings } from "../domain/types.ts";
import type { VersionEntry } from "../domain/oplog.ts";
import {
  validateConcept, countSentences, deriveTitle, isOverdue, linkCountFor,
  SUMMARY_MAX_CHARS, NOTE_MIN_CHARS, statusCode, SEARCH_WEIGHT, DAY_MS,
} from "../domain/rules.ts";
import type { RuleCode, SearchResult } from "../domain/rules.ts";
import type { ConceptPatch } from "../data/repository.ts";
import { seedOriginalName } from "../domain/seed.ts";
import { useI18n, localizedCategoryName } from "../i18n/index.tsx";
import { LinkComposer } from "./graphViews.tsx";
import type { MessageKey, Translate, Params } from "../i18n/index.tsx";

/** 領域層給代碼，這裡負責查表 —— 這是整套設計裡最重要的一條分界。 */
export function useRuleText(): (code: RuleCode | null, params?: Params) => string {
  const { t } = useI18n();
  return (code, params) => (code ? t(code as MessageKey, params) : "");
}

export function useCategoryName(): (c: Category | undefined) => string {
  const { t } = useI18n();
  return (c) => (c ? localizedCategoryName(c, t, seedOriginalName) : "—");
}

/** 標本櫃只放主類別：副類別是抽屜裡的隔板，選了主類別之後才出現（見 App 的 subchips）。 */
export function Cabinet(props: {
  categories: Category[]; concepts: Concept[]; catId: string; onPick: (id: string) => void;
  onManage: () => void;
}) {
  const { t, tn } = useI18n();
  const nameOf = useCategoryName();
  /* 「在不在這個抽屜裡」＝歸位在它裡面，或被標上它的副類別 —— 與類別管理面板同一條定義。 */
  const count = (id: string) => props.concepts.filter(
    (c) => c.primaryCategoryId === id || c.subCategoryIds.includes(id)).length;
  return (
    <nav className="cabinet" aria-label={t("cabinet.label")}>
      {props.categories.filter((c) => !c.parentId).map((c) => (
        <button key={c.id} className="drawer" aria-pressed={c.id === props.catId}
          onClick={() => props.onPick(c.id)}>
          <span className="dn">{nameOf(c)}</span>
          <span className="dc mono">{tn("cabinet.count", count(c.id))}</span>
        </button>
      ))}
      <button className="drawer manage" onClick={props.onManage} data-cabinet-manage>
        <span className="dn">{t("cat.manage")}</span>
        <span className="dc mono">{t("cat.manage.sub")}</span>
      </button>
    </nav>
  );
}

export function SheetView(props: {
  concept: Concept; categories: Category[]; links: KnowledgeLink[]; allConcepts: Concept[];
  onSave: (patch: ConceptPatch) => void; onTrash: () => void;
  onHistory: () => void; onGraph: () => void;
  onCreateLink: (toId: string, type: string, note: string) => void;
  onDeleteLink: (id: string) => void;
}) {
  const { t, formatDate, formatNumber } = useI18n();
  const rule = useRuleText();
  const nameOf = useCategoryName();
  const { concept } = props;
  const [editing, setEditing] = useState(false);
  const [bodyOpen, setBodyOpen] = useState(false);
  const [composerOpen, setComposerOpen] = useState(false);
  const check = validateConcept({
    summary: concept.summary, note: concept.note, primaryCategoryId: concept.primaryCategoryId });
  const invalid = !check.ok;
  const overdue = isOverdue(concept, Date.now());
  const strings = props.links.filter((l) => l.fromId === concept.id || l.toId === concept.id);
  const cap = props.categories.find((c) => c.id === concept.primaryCategoryId)?.linkCapPerNode ?? 12;

  if (editing) {
    return <EditForm concept={concept} categories={props.categories}
      onCancel={() => setEditing(false)}
      onSave={(patch) => { props.onSave(patch); setEditing(false); }} />;
  }

  return (
    <article className={"sheet" + (overdue ? " due" : "") + (invalid ? " invalid" : "")}>
      <h1>{concept.title || deriveTitle(concept.summary)}</h1>
      <p className="spec"><span className="sent">{concept.summary}</span></p>
      <p className="hint">
        {t(statusCode(concept.status))}
        <span className="mono"> · {t("sheet.meta", {
          sentences: formatNumber(countSentences(concept.summary)),
          chars: formatNumber(concept.summary.length),
          max: formatNumber(SUMMARY_MAX_CHARS),
          rev: formatNumber(concept.rev),
        })}</span>
        {check.warnings.map((w) => <span key={w.field}> · {rule(w.code, w.params)}</span>)}
      </p>

      <div className="sheettools">
        <button className="btn" onClick={() => setEditing(true)}>{t("sheet.edit")}</button>
        <button className="btn" onClick={() => setBodyOpen((v) => !v)}>
          {bodyOpen ? t("sheet.body.hide") : t("sheet.body.show")}</button>
        <button className="btn" onClick={props.onHistory}>{t("sheet.history")}</button>
        <button className="btn" onClick={props.onGraph}>{t("graph.title")}</button>
        <button className="btn" onClick={props.onTrash}>{t("sheet.trash")}</button>
      </div>

      {bodyOpen && <p className="body">{concept.body || t("sheet.body.empty")}</p>}

      <div className={"labelplate" + (invalid ? " invalid" : "")}>
        <div className="lh">
          <span>{t("field.note")}{invalid ? t("sheet.invalid.from") : ""}</span>
          <span className="mono">{invalid ? t("sheet.invalid.badge") : "HS-" + concept.id.slice(-6)}</span>
        </div>
        <div className="rows">
          <span className="meta-k">{t("sheet.note.label")}</span>
          <span>{concept.note || t("sheet.note.empty")}</span>
          <span className="meta-k">{t("sheet.source")}</span><span>{concept.source || "—"}</span>
          <span className="meta-k">{t("sheet.due")}</span>
          <span className="mono">{formatDate(concept.reviewDueAt)}</span>
          <span className="meta-k">{t("sheet.category")}</span>
          <span>{nameOf(props.categories.find((c) => c.id === concept.primaryCategoryId))}</span>
          {concept.subCategoryIds.length > 0 && (
            <>
              <span className="meta-k">{t("field.subcategories")}</span>
              <span data-sheet-subs>{concept.subCategoryIds.map((id) => nameOf(
                props.categories.find((c) => c.id === id))).join("、")}</span>
            </>
          )}
        </div>
        {invalid && (
          <p className="req" style={{ color: "var(--warn)" }}>
            {t("sheet.invalid.warning", { min: NOTE_MIN_CHARS })}
          </p>
        )}
      </div>

      <section className="strings">
        <h2>{t("sheet.strings.head", { n: strings.length, cap })}</h2>
        {strings.length === 0 && <p className="hint">{t("sheet.strings.empty")}</p>}
        {strings.map((l) => {
          const otherId = l.fromId === concept.id ? l.toId : l.fromId;
          const other = props.allConcepts.find((c) => c.id === otherId);
          /* 找不到對端有兩種可能：它被鎖住了，或它被刪了。兩種都不該洩漏任何線索 ——
             所以連 id 的片段都不顯示，只給一顆灰色節點。（這是設計文件裡最嚴格的一條規則。） */
          const restricted = !other;
          return (
            <div key={l.id} className="string" data-string={l.id} data-restricted={restricted}>
              <span className="tag mono">{l.type}</span>
              <span className={"to" + (restricted ? " restricted" : "")} data-string-target>
                {restricted ? t("sheet.strings.restricted")
                  : (l.fromId === concept.id ? "→ " : "← ") + deriveTitle(other!.summary)
                    + (l.note ? "（" + l.note + "）" : "")}
              </span>
              <button className="chip" data-string-cut={l.id}
                onClick={() => props.onDeleteLink(l.id)}>{t("link.delete")}</button>
            </div>
          );
        })}
        {composerOpen
          ? <LinkComposer current={concept} concepts={props.allConcepts} links={props.links} cap={cap}
              onCreate={(toId, type, note) => { props.onCreateLink(toId, type, note); setComposerOpen(false); }}
              onCancel={() => setComposerOpen(false)} />
          : (
            <div className="row" style={{ marginTop: 12 }}>
              <button className="btn" data-link-new onClick={() => setComposerOpen(true)}>{t("link.new")}</button>
            </div>
          )}
      </section>
    </article>
  );
}

function EditForm(props: {
  concept: Concept; categories: Category[]; onSave: (patch: ConceptPatch) => void; onCancel: () => void;
}) {
  const { t } = useI18n();
  const base: ConceptPatch = {
    title: props.concept.title, summary: props.concept.summary, body: props.concept.body,
    note: props.concept.note, source: props.concept.source,
    primaryCategoryId: props.concept.primaryCategoryId, subCategoryIds: props.concept.subCategoryIds,
    nsfw: props.concept.nsfw, reviewIntervalDays: props.concept.reviewIntervalDays,
  };
  const [draft, setDraft] = useState<ConceptPatch>(base);
  const dirty = JSON.stringify(draft) !== JSON.stringify(base);
  const check = validateConcept({
    summary: draft.summary ?? "", note: draft.note ?? "", primaryCategoryId: draft.primaryCategoryId ?? "" });
  return (
    <article className="sheet">
      <h1>{t("edit.title", { title: props.concept.title || deriveTitle(props.concept.summary) })}</h1>
      <Fields draft={draft} setDraft={setDraft} categories={props.categories} check={check} />
      <div className="sheettools">
        <button className="btn primary" disabled={!dirty} onClick={() => props.onSave(draft)}>
          {dirty ? t("edit.save") : t("toast.noChange")}</button>
        <button className="btn" onClick={props.onCancel}>{t("edit.cancel")}</button>
      </div>
    </article>
  );
}

function Fields(props: {
  draft: ConceptPatch; setDraft: (d: ConceptPatch) => void; categories: Category[];
  check: { ok: boolean; errors: { field: string; code: RuleCode; params?: Params }[];
    warnings: { field: string; code: RuleCode; params?: Params }[] };
}) {
  const { t, tn, formatNumber } = useI18n();
  const rule = useRuleText();
  const { draft, setDraft, check } = props;
  const set = (patch: ConceptPatch) => setDraft({ ...draft, ...patch });
  const sentences = countSentences(draft.summary ?? "");
  return (
    <>
      <section className="panel">
        <h2><span>{t("field.summary")}</span>
          <span className={"mono counter" + (sentences > 2 ? " over" : "")}>
            {formatNumber(sentences)} / 2 · {formatNumber((draft.summary ?? "").length)} / {formatNumber(SUMMARY_MAX_CHARS)}
          </span></h2>
        <textarea aria-label={t("field.summary.aria")} value={draft.summary ?? ""}
          onChange={(e) => set({ summary: e.target.value })} />
        {check.warnings.map((w) => <p key={w.field} className="hint bad">{rule(w.code, w.params)}</p>)}
      </section>
      <section className="panel">
        <h2><span>{t("field.note")}</span>
          <span className="mono counter">{formatNumber((draft.note ?? "").length)} / {formatNumber(NOTE_MIN_CHARS)}</span></h2>
        <textarea aria-label={t("field.note.aria")} value={draft.note ?? ""}
          onChange={(e) => set({ note: e.target.value })} placeholder={t("field.note.placeholder")} />
        <div className="row" style={{ marginTop: 12 }}>
          <input className="input" aria-label={t("field.source")} placeholder={t("field.source")}
            value={draft.source ?? ""} onChange={(e) => set({ source: e.target.value })} />
          <select className="input" aria-label={t("field.category")} value={draft.primaryCategoryId ?? ""}
            data-field-category
            onChange={(e) => set({
              primaryCategoryId: e.target.value,
              /* 換抽屜時舊的隔板沒有意義了，直接丟掉 —— 留著會出現
                 「歸位在 A，卻標著 B 的隔板」這種沒人看得懂的状态。 */
              subCategoryIds: (draft.subCategoryIds ?? []).filter(
                (id) => props.categories.find((c) => c.id === id)?.parentId === e.target.value),
            })}>
            {props.categories.filter((c) => !c.parentId).map((c) => <CategoryOption key={c.id} category={c} />)}
          </select>
          <select className="input" aria-label={t("field.interval")} value={String(draft.reviewIntervalDays ?? 365)}
            onChange={(e) => set({ reviewIntervalDays: Number(e.target.value) })}>
            {[30, 90, 180, 365, 730, 1825].map((d) => (
              <option key={d} value={d}>{tn("field.days", d)}</option>))}
          </select>
        </div>
        <SubCategoryChips draft={draft} setDraft={props.setDraft} categories={props.categories} />
        <div className="row" style={{ marginTop: 12 }}>
          <label className="chip">
            <input type="checkbox" checked={!!draft.nsfw} onChange={(e) => set({ nsfw: e.target.checked })} />
            {" "}{t("field.nsfw")}
          </label>
        </div>
        <p className="hint">{t("field.body")}</p>
        <textarea aria-label={t("field.body.aria")} value={draft.body ?? ""}
          onChange={(e) => set({ body: e.target.value })} />
      </section>
      {check.errors.map((e) => <p key={e.field} className="hint bad">{rule(e.code, e.params)}</p>)}
    </>
  );
}

/** 副類別的挑選：可多選、可全部不選。沒有隔板時直接告訴你怎麼加一個，
    而不是給一個空白的下拉選單讓你猜。 */
function SubCategoryChips(props: {
  draft: ConceptPatch; setDraft: (d: ConceptPatch) => void; categories: Category[];
}) {
  const { t } = useI18n();
  const nameOf = useCategoryName();
  const subs = props.categories.filter((c) => c.parentId === (props.draft.primaryCategoryId ?? ""));
  const chosen = props.draft.subCategoryIds ?? [];
  return (
    <div style={{ marginTop: 12 }} data-sub-picker>
      <span className="hint" style={{ margin: 0 }}>{t("field.subcategories")}</span>
      {subs.length === 0
        ? <p className="hint dim" style={{ margin: "4px 0 0" }}>{t("field.subcategories.none")}</p>
        : (
          <div className="subchips">
            {subs.map((s) => {
              const on = chosen.includes(s.id);
              return (
                <button type="button" key={s.id} className="chip" aria-pressed={on} data-sub-option={s.id}
                  onClick={() => props.setDraft({ ...props.draft, subCategoryIds: on
                    ? chosen.filter((x) => x !== s.id) : [...chosen, s.id] })}>{nameOf(s)}</button>
              );
            })}
          </div>
        )}
    </div>
  );
}

function CategoryOption({ category }: { category: Category }) {
  const nameOf = useCategoryName();
  return <option value={category.id}>{nameOf(category)}</option>;
}

export function Workbench(props: {
  categories: Category[]; defaultCategoryId: string; onCreate: (patch: ConceptPatch) => void;
  /** 從托盤「壓製」過來時，來源預填成那一包素材的名字。 */
  initialSource?: string;
}) {
  const { t } = useI18n();
  const [draft, setDraft] = useState<ConceptPatch>({
    summary: "", note: "", source: props.initialSource ?? "", body: "",
    primaryCategoryId: props.defaultCategoryId, subCategoryIds: [], reviewIntervalDays: 365, nsfw: false,
  });
  useEffect(() => { setDraft((d) => ({ ...d, primaryCategoryId: props.defaultCategoryId })); },
    [props.defaultCategoryId]);
  useEffect(() => {
    if (props.initialSource) setDraft((d) => ({ ...d, source: props.initialSource! }));
  }, [props.initialSource]);
  const check = validateConcept({
    summary: draft.summary ?? "", note: draft.note ?? "", primaryCategoryId: draft.primaryCategoryId ?? "" });
  return (
    <div className="workbench" style={{ padding: 0 }}>
      <section className="panel">
        <h2><span>{t("workbench.title")}</span><span className="mono dim">{t("workbench.subtitle")}</span></h2>
        <p className="hint">{t("workbench.hint")}</p>
      </section>
      <Fields draft={draft} setDraft={setDraft} categories={props.categories} check={check} />
      <section className="panel" style={{ position: "sticky", bottom: 0 }}>
        <div className="row">
          <button className="btn primary" disabled={!check.ok} onClick={() => props.onCreate(draft)}>
            {t("workbench.save")}</button>
          <button className="btn" onClick={() => props.onCreate({ ...draft, status: "draft" })}>
            {t("workbench.draft")}</button>
        </div>
        <FieldsHint check={check} />
      </section>
    </div>
  );
}

function FieldsHint(props: { check: { ok: boolean; errors: { code: RuleCode; params?: Params }[] } }) {
  const { t } = useI18n();
  const rule = useRuleText();
  const first = props.check.errors[0];
  return (
    <p className={"hint " + (props.check.ok ? "ok" : "bad")}>
      {props.check.ok ? t("workbench.ok") : (first ? rule(first.code, first.params) : "")}
    </p>
  );
}

export function HistoryPanel(props: {
  concept: Concept; versions: VersionEntry[]; onRestore: (rev: number) => void; onClose: () => void;
}) {
  const { t, formatDateTime } = useI18n();
  return (
    <section className="sheetpanel">
      <div className="row between">
        <h2>{t("history.title", { title: deriveTitle(props.concept.summary) })}</h2>
        <button className="iconbtn" onClick={props.onClose} aria-label={t("settings.title")}>✕</button>
      </div>
      <p className="hint">{t("history.hint")}</p>
      {props.versions.map((v) => (
        <div key={v.rev} className="string">
          <span className="tag mono">{t("history.version", { n: v.rev })}</span>
          <span className="to mono dim">
            {formatDateTime(v.at)}{v.fields.length ? "・" + v.fields.join("、") : "・" + t("history.current")}
          </span>
          {v.rev !== props.concept.rev && (
            <button className="chip" onClick={() => props.onRestore(v.rev)}>{t("history.restore")}</button>)}
        </div>
      ))}
    </section>
  );
}

export function ReviewPanel(props: {
  due: Concept[]; index: number; onKeep: () => void; onEdit: () => void; onTrash: () => void; onClose: () => void;
}) {
  const { t, tn, formatDate } = useI18n();
  const c = props.due[props.index];
  return (
    <section className="sheetpanel">
      <div className="row between">
        <h2>{t("review.title")}</h2>
        <button className="iconbtn" onClick={props.onClose} aria-label={t("settings.title")}>✕</button>
      </div>
      {!c && <p className="hint">{t("review.none")}</p>}
      {c && (
        <>
          <p className="hint">{t("review.hint")}</p>
          <div className="panel" style={{ margin: "0 0 16px" }}>
            <p className="mono dim">{t("review.progress", { index: props.index + 1, total: props.due.length })}</p>
            <h2 style={{ display: "block" }}>{deriveTitle(c.summary)}</h2>
            <p className="spec">{c.summary}</p>
            <p className="hint">{t("review.due", {
              date: formatDate(c.reviewDueAt),
              days: tn("field.days", Math.floor((Date.now() - c.reviewDueAt) / DAY_MS)),
            })}</p>
          </div>
          <div className="row">
            <button className="btn primary" onClick={props.onKeep}>
              {t("review.keep", { interval: tn("field.days", c.reviewIntervalDays) })}</button>
            <button className="btn" onClick={props.onEdit}>{t("review.edit")}</button>
            <button className="btn" onClick={props.onTrash}>{t("review.trash")}</button>
          </div>
        </>
      )}
    </section>
  );
}

export function SearchPanel(props: {
  results: SearchResult[]; query: string; onQuery: (q: string) => void;
  adultUnlocked: boolean; onPick: (c: Concept) => void; onClose: () => void;
}) {
  const { t, tn } = useI18n();
  return (
    <section className="sheetpanel">
      <div className="row between">
        <h2>{t("search.title")}</h2>
        <button className="iconbtn" onClick={props.onClose} aria-label={t("settings.title")}>✕</button>
      </div>
      <input className="searchfield" autoFocus value={props.query} aria-label={t("search.aria")}
        placeholder={t("search.placeholder")} onChange={(e) => props.onQuery(e.target.value)} />
      <p className="hint">
        {props.query.trim() ? tn("search.results", props.results.length) : t("search.prompt")}
        {!props.adultUnlocked && "　・" + t("search.locked")}
      </p>
      {props.results.map((r) => (
        <button key={r.concept.id} className="result" onClick={() => props.onPick(r.concept)}>
          <span>{r.concept.title || deriveTitle(r.concept.summary)}</span>
          <br /><span className="rk mono">
            {r.concept.source || "—"}　{t("search.hitWeight", { score: r.score })}</span>
        </button>
      ))}
      {props.query.trim() && props.results.length === 0 && <p className="hint dim">{t("search.none")}</p>}
      <p className="hint dim" style={{ marginTop: 16 }}>
        {t("search.weights", {
          title: SEARCH_WEIGHT["title"]!, summary: SEARCH_WEIGHT["summary"]!, note: SEARCH_WEIGHT["note"]!,
          source: SEARCH_WEIGHT["source"]!, body: SEARCH_WEIGHT["body"]!,
        })}
      </p>
    </section>
  );
}

export function TrashPanel(props: {
  items: Concept[]; onRestore: (c: Concept) => void; onPurge: () => void; onClose: () => void;
}) {
  const { t, tn } = useI18n();
  return (
    <section className="sheetpanel">
      <div className="row between">
        <h2>{t("trash.title")}</h2>
        <button className="iconbtn" onClick={props.onClose} aria-label={t("settings.title")}>✕</button>
      </div>
      <p className="hint">{t("trash.hint")}</p>
      {props.items.length === 0 && <p className="hint dim">{t("trash.empty")}</p>}
      {props.items.map((c) => (
        <div key={c.id} className="string">
          <span className="to">{deriveTitle(c.summary)}</span>
          <span className="mono dim">{c.deletedAt
            ? t("trash.daysLeft", { days: tn("field.days", Math.max(0, 30 - Math.floor((Date.now() - c.deletedAt) / DAY_MS))) })
            : ""}</span>
          <button className="chip" onClick={() => props.onRestore(c)}>{t("trash.restore")}</button>
        </div>
      ))}
      {props.items.length > 0 && (
        <div className="row" style={{ marginTop: 16 }}>
          <button className="btn" onClick={props.onPurge}>{t("trash.emptyAll")}</button>
        </div>)}
    </section>
  );
}

export function SettingsPanel(props: {
  settings: Settings; onSave: (p: Partial<Settings>) => void; onClose: () => void;
  children?: ReactNode;
}) {
  const { t } = useI18n();
  return (
    <section className="sheetpanel">
      <div className="row between">
        <h2>{t("settings.title")}</h2>
        <button className="iconbtn" onClick={props.onClose} aria-label={t("settings.title")}>✕</button>
      </div>
      <div className="panel">
        <h2 style={{ display: "block" }}>{t("settings.language")}</h2>
        <div className="pickrow">
          {([["auto", t("settings.language.auto")], ["zh-Hant", t("app.locale.zh")], ["en", t("app.locale.en")]] as const)
            .map(([value, label]) => (
              <button key={value} className="chip" aria-pressed={props.settings.locale === value}
                onClick={() => props.onSave({ locale: value })}>{label}</button>))}
        </div>
        <h2 style={{ display: "block", marginTop: 20 }}>{t("settings.material")}</h2>
        <div className="pickrow">
          {(["night", "day"] as const).map((m) => (
            <button key={m} className="chip" aria-pressed={props.settings.sheetMaterial === m}
              onClick={() => props.onSave({ sheetMaterial: m })}>
              {m === "night" ? t("settings.material.night") : t("settings.material.day")}</button>))}
        </div>
        <h2 style={{ display: "block", marginTop: 20 }}>{t("settings.typeScale")}</h2>
        <div className="pickrow">
          {([[1, t("settings.scale.small")], [1.15, t("settings.scale.normal")], [1.3, t("settings.scale.large")]] as const)
            .map(([f, label]) => (
              <button key={f} className="chip" aria-pressed={props.settings.typeScale === f}
                onClick={() => props.onSave({ typeScale: f })}>{label}</button>))}
        </div>
        <h2 style={{ display: "block", marginTop: 20 }}>{t("settings.adult")}</h2>
        <div className="pickrow">
          <button className="chip" aria-pressed={props.settings.adultUnlocked}
            onClick={() => props.onSave({ adultUnlocked: !props.settings.adultUnlocked })}>
            {props.settings.adultUnlocked ? t("settings.adult.unlocked") : t("settings.adult.locked")}</button>
        </div>
        <p className="hint">{t("settings.adult.hint")}</p>
        <p className="hint mono">{props.settings.deviceId}</p>
      </div>
      {props.children}
    </section>
  );
}

export function Rail(props: {
  concept: Concept | null; links: KnowledgeLink[]; due: number; inbox: number; catName: string;
  counts: { label: string; value: string }[]; onReview: () => void;
  links_total?: number; onGraph?: () => void;
}) {
  const { t } = useI18n();
  const strings = props.concept
    ? props.links.filter((l) => l.fromId === props.concept!.id || l.toId === props.concept!.id) : [];
  return (
    <aside className="rail">
      {props.concept && (
        <>
          <h2 className="blockhead">{t("rail.strings", { n: strings.length, cap: 12 })}</h2>
          {strings.map((l) => (
            <div key={l.id} className="string">
              <span className="tag mono">{l.type}</span>
              <span className="to">{l.toId === props.concept!.id ? "← " : "→ "}
                {l.note || t("sheet.strings.restricted")}</span>
            </div>))}
        </>
      )}
      <h2 className="blockhead" style={{ marginTop: 32 }}>
        <button className="chip" onClick={props.onReview}>{t("rail.due", { n: props.due })}</button>
        {(props.links_total ?? 0) >= 0 && (
          <button className="chip" data-open-graph onClick={props.onGraph}>{t("graph.title")}</button>)}
      </h2>
      <h2 className="blockhead" style={{ marginTop: 32 }}>{t("rail.tray")}</h2>
      <div className="mono dim">{props.counts.length >= 0 ? props.inbox : 0}</div>
      <h2 className="blockhead" style={{ marginTop: 32 }}>{t("rail.status")}</h2>
      <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", gap: "2px 12px" }}>
        <span className="meta-k dim">{t("rail.category")}</span><span className="mono">{props.catName}</span>
        {props.counts.map((c) => (
          <span key={c.label} style={{ display: "contents" }}>
            <span className="meta-k dim">{c.label}</span><span className="mono">{c.value}</span>
          </span>))}
      </div>
    </aside>
  );
}

export function useToast(): [string, (m: string) => void] {
  const [msg, setMsg] = useState("");
  useEffect(() => {
    if (!msg) return;
    const t = setTimeout(() => setMsg(""), 2600);
    return () => clearTimeout(t);
  }, [msg]);
  return [msg, setMsg];
}

export function Toast({ message }: { message: string }) {
  if (!message) return null;
  return <div className="toast">{message}</div>;
}

export function linkCount(links: KnowledgeLink[], id: string): number {
  return linkCountFor(links, id);
}

export type { Translate };